import { ReactNode, useEffect, useState } from 'react'
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material'
import { IntlProvider } from 'react-intl'
import { QueryClient, QueryClientProvider } from 'react-query'

import { sharedDashboardUuid } from 'utils/dashboardSharing'
import { publicDashboardRequest, ShareMode } from './sharedDashboardApi'
import {
  SharedDashboardLayout,
  SharedDashboardLoading,
  SharedDashboardUnavailable,
  SharedDashboardViewer,
} from './SharedDashboard'
import {
  sharingEnglish,
  sharingPortuguese,
} from 'i18n/dashboardSharingMessages'

// No User, Company or authenticated request providers are mounted until the mode is known.
export default function SharedDashboardEntry({
  restrictedApp,
}: {
  restrictedApp: ReactNode
}) {
  const uuid = sharedDashboardUuid(window.location.pathname)
  const [mode, setMode] = useState<ShareMode>()
  const [failed, setFailed] = useState(!uuid)
  const [client] = useState(() => new QueryClient())
  const [theme] = useState(() => createTheme())
  const portuguese = navigator.language.toLowerCase().startsWith('pt')
  const messages = Object.fromEntries(
    Object.entries(portuguese ? sharingPortuguese : sharingEnglish).map(
      ([key, value]) => [`dashboardSharing.${key}`, value],
    ),
  )

  useEffect(() => {
    if (!uuid) return undefined
    const controller = new AbortController()
    publicDashboardRequest<{ mode: ShareMode }>(
      `${uuid}/access`,
      controller.signal,
    )
      .then(result => {
        if (result.mode !== 'public' && result.mode !== 'restricted')
          throw new Error('invalid_mode')
        setMode(result.mode)
      })
      .catch(() => {
        if (!controller.signal.aborted) setFailed(true)
      })
    return () => controller.abort()
  }, [uuid])

  if (mode === 'restricted') return <>{restrictedApp}</>
  return (
    <IntlProvider locale={portuguese ? 'pt-BR' : 'en-US'} messages={messages}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <QueryClientProvider client={client}>
          {mode === 'public' && uuid ? (
            <SharedDashboardViewer key={uuid} uuid={uuid} mode='public' />
          ) : (
            <SharedDashboardLayout>
              {failed ? (
                <SharedDashboardUnavailable />
              ) : (
                <SharedDashboardLoading />
              )}
            </SharedDashboardLayout>
          )}
        </QueryClientProvider>
      </ThemeProvider>
    </IntlProvider>
  )
}
